import * as XLSX from "xlsx-js-style";
import { IAnalysisSummaryResponse } from "../interface/analysisSummary";

const formatCurrencyForExcel = (value: number | undefined): string => {
  if (value === undefined || value === null) return "$-";
  return `$${value.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};

const formatPercentageForExcel = (value: number | undefined): string => {
  if (value === undefined || value === null) return "-%";
  return `${value.toFixed(2)}%`;
};

const isNegativeValue = (value: string | number | undefined): boolean => {
  if (value === undefined || value === null) return false;

  // Check if it's a number and negative
  if (typeof value === "number") return value < 0;

  // Check if it's a string with a negative number
  if (typeof value === "string") {
    // Extract number from formatted string (e.g., "$-1,234" or "-5.00%")
    const numMatch = value.match(/-?\d+[\d,.]*/);
    if (numMatch) {
      const num = parseFloat(numMatch[0].replace(/,/g, ""));
      return num < 0;
    }
  }

  return false;
};

export const exportAnalysisToExcel = (analysisSummary: IAnalysisSummaryResponse, propertyAddress?: string): void => {
  const workbook = XLSX.utils.book_new();

  // Create data array for the spreadsheet
  const data: any[] = [];

  // Add header row with property info
  data.push([`Results for ${propertyAddress || "Property Analysis"}`]);
  data.push([]); // Empty row

  // Add all variables and summary inputs as vertical list (one per row)
  data.push([
    "Loan to value ratio target %",
    analysisSummary.Levers?.LoanValueRatio ? `${analysisSummary.Levers.LoanValueRatio}%` : "",
  ]);

  data.push([
    "10yr internal rate of return",
    analysisSummary.SummaryInput?.TenYrInternalRate
      ? `${analysisSummary.SummaryInput.TenYrInternalRate.toFixed(2)}%`
      : "",
  ]);

  data.push(["Property purchase price", formatCurrencyForExcel(analysisSummary.SummaryInput?.PurchasePrice)]);

  data.push(["Deposit", formatCurrencyForExcel(analysisSummary.SummaryInput?.Deposit)]);

  data.push(["Purchase costs", formatCurrencyForExcel(analysisSummary.SummaryInput?.PurchaseCost)]);

  data.push([
    "10yr total growth",
    analysisSummary.SummaryInput?.TenYrTotalGrowth
      ? `${analysisSummary.SummaryInput.TenYrTotalGrowth.toFixed(2)}%`
      : "",
  ]);

  data.push(["Loan cost", formatCurrencyForExcel(analysisSummary.SummaryInput?.LoanCoast)]);

  data.push([]); // Empty row

  // Year headers
  const yearHeaders = ["Years", "Today", "1", "2", "3", "4", "5", "6", "7", "8", "9", "10"];
  data.push(yearHeaders);

  // Overview section
  data.push(["Overview"]);
  data.push([
    "Capital growth rate %",
    formatPercentageForExcel(analysisSummary.AnalysisOverview.cgrToday),
    formatPercentageForExcel(analysisSummary.AnalysisOverview.cgrYr1),
    formatPercentageForExcel(analysisSummary.AnalysisOverview.cgrYr2),
    formatPercentageForExcel(analysisSummary.AnalysisOverview.cgrYr3),
    formatPercentageForExcel(analysisSummary.AnalysisOverview.cgrYr4),
    formatPercentageForExcel(analysisSummary.AnalysisOverview.cgrYr5),
    formatPercentageForExcel(analysisSummary.AnalysisOverview.cgrYr6),
    formatPercentageForExcel(analysisSummary.AnalysisOverview.cgrYr7),
    formatPercentageForExcel(analysisSummary.AnalysisOverview.cgrYr8),
    formatPercentageForExcel(analysisSummary.AnalysisOverview.cgrYr9),
    formatPercentageForExcel(analysisSummary.AnalysisOverview.cgrYr10),
  ]);
  data.push([
    "Consumer price index %",
    formatPercentageForExcel(analysisSummary.AnalysisOverview.cpiToday),
    formatPercentageForExcel(analysisSummary.AnalysisOverview.cpiYr1),
    formatPercentageForExcel(analysisSummary.AnalysisOverview.cpiYr2),
    formatPercentageForExcel(analysisSummary.AnalysisOverview.cpiYr3),
    formatPercentageForExcel(analysisSummary.AnalysisOverview.cpiYr4),
    formatPercentageForExcel(analysisSummary.AnalysisOverview.cpiYr5),
    formatPercentageForExcel(analysisSummary.AnalysisOverview.cpiYr6),
    formatPercentageForExcel(analysisSummary.AnalysisOverview.cpiYr7),
    formatPercentageForExcel(analysisSummary.AnalysisOverview.cpiYr8),
    formatPercentageForExcel(analysisSummary.AnalysisOverview.cpiYr9),
    formatPercentageForExcel(analysisSummary.AnalysisOverview.cpiYr10),
  ]);
  data.push([
    "Property market value $",
    formatCurrencyForExcel(analysisSummary.AnalysisOverview.marketValueToday),
    formatCurrencyForExcel(analysisSummary.AnalysisOverview.marketValueYr1),
    formatCurrencyForExcel(analysisSummary.AnalysisOverview.marketValueYr2),
    formatCurrencyForExcel(analysisSummary.AnalysisOverview.marketValueYr3),
    formatCurrencyForExcel(analysisSummary.AnalysisOverview.marketValueYr4),
    formatCurrencyForExcel(analysisSummary.AnalysisOverview.marketValueYr5),
    formatCurrencyForExcel(analysisSummary.AnalysisOverview.marketValueYr6),
    formatCurrencyForExcel(analysisSummary.AnalysisOverview.marketValueYr7),
    formatCurrencyForExcel(analysisSummary.AnalysisOverview.marketValueYr8),
    formatCurrencyForExcel(analysisSummary.AnalysisOverview.marketValueYr9),
    formatCurrencyForExcel(analysisSummary.AnalysisOverview.marketValueYr10),
  ]);
  data.push([
    "Amount of loan $",
    formatCurrencyForExcel(analysisSummary.AnalysisOverview.AmountLoanToday),
    formatCurrencyForExcel(analysisSummary.AnalysisOverview.AmountLoanYr1),
    formatCurrencyForExcel(analysisSummary.AnalysisOverview.AmountLoanYr2),
    formatCurrencyForExcel(analysisSummary.AnalysisOverview.AmountLoanYr3),
    formatCurrencyForExcel(analysisSummary.AnalysisOverview.AmountLoanYr4),
    formatCurrencyForExcel(analysisSummary.AnalysisOverview.AmountLoanYr5),
    formatCurrencyForExcel(analysisSummary.AnalysisOverview.AmountLoanYr6),
    formatCurrencyForExcel(analysisSummary.AnalysisOverview.AmountLoanYr7),
    formatCurrencyForExcel(analysisSummary.AnalysisOverview.AmountLoanYr8),
    formatCurrencyForExcel(analysisSummary.AnalysisOverview.AmountLoanYr9),
    formatCurrencyForExcel(analysisSummary.AnalysisOverview.AmountLoanYr10),
  ]);
  data.push([]); // Empty row

  // Statistics section
  data.push(["Statistics"]);
  data.push([
    "Equity in property $",
    formatCurrencyForExcel(analysisSummary.AnalysisStatistics.EquityInPropertyToday),
    formatCurrencyForExcel(analysisSummary.AnalysisStatistics.EquityInPropertyYr1),
    formatCurrencyForExcel(analysisSummary.AnalysisStatistics.EquityInPropertyYr2),
    formatCurrencyForExcel(analysisSummary.AnalysisStatistics.EquityInPropertyYr3),
    formatCurrencyForExcel(analysisSummary.AnalysisStatistics.EquityInPropertyYr4),
    formatCurrencyForExcel(analysisSummary.AnalysisStatistics.EquityInPropertyYr5),
    formatCurrencyForExcel(analysisSummary.AnalysisStatistics.EquityInPropertyYr6),
    formatCurrencyForExcel(analysisSummary.AnalysisStatistics.EquityInPropertyYr7),
    formatCurrencyForExcel(analysisSummary.AnalysisStatistics.EquityInPropertyYr8),
    formatCurrencyForExcel(analysisSummary.AnalysisStatistics.EquityInPropertyYr9),
    formatCurrencyForExcel(analysisSummary.AnalysisStatistics.EquityInPropertyYr10),
  ]);
  data.push([
    "Loan to value ratio %",
    formatPercentageForExcel(analysisSummary.AnalysisStatistics.LoanValueRatioToday),
    formatPercentageForExcel(analysisSummary.AnalysisStatistics.LoanValueRatioYr1),
    formatPercentageForExcel(analysisSummary.AnalysisStatistics.LoanValueRatioYr2),
    formatPercentageForExcel(analysisSummary.AnalysisStatistics.LoanValueRatioYr3),
    formatPercentageForExcel(analysisSummary.AnalysisStatistics.LoanValueRatioYr4),
    formatPercentageForExcel(analysisSummary.AnalysisStatistics.LoanValueRatioYr5),
    formatPercentageForExcel(analysisSummary.AnalysisStatistics.LoanValueRatioYr6),
    formatPercentageForExcel(analysisSummary.AnalysisStatistics.LoanValueRatioYr7),
    formatPercentageForExcel(analysisSummary.AnalysisStatistics.LoanValueRatioYr8),
    formatPercentageForExcel(analysisSummary.AnalysisStatistics.LoanValueRatioYr9),
    formatPercentageForExcel(analysisSummary.AnalysisStatistics.LoanValueRatioYr10),
  ]);
  data.push([
    "Surplus equity to reinvest $",
    formatCurrencyForExcel(analysisSummary.AnalysisStatistics.SurplusEquitytoReinvestToday),
    formatCurrencyForExcel(analysisSummary.AnalysisStatistics.SurplusEquitytoReinvestYr1),
    formatCurrencyForExcel(analysisSummary.AnalysisStatistics.SurplusEquitytoReinvestYr2),
    formatCurrencyForExcel(analysisSummary.AnalysisStatistics.SurplusEquitytoReinvestYr3),
    formatCurrencyForExcel(analysisSummary.AnalysisStatistics.SurplusEquitytoReinvestYr4),
    formatCurrencyForExcel(analysisSummary.AnalysisStatistics.SurplusEquitytoReinvestYr5),
    formatCurrencyForExcel(analysisSummary.AnalysisStatistics.SurplusEquitytoReinvestYr6),
    formatCurrencyForExcel(analysisSummary.AnalysisStatistics.SurplusEquitytoReinvestYr7),
    formatCurrencyForExcel(analysisSummary.AnalysisStatistics.SurplusEquitytoReinvestYr8),
    formatCurrencyForExcel(analysisSummary.AnalysisStatistics.SurplusEquitytoReinvestYr9),
    formatCurrencyForExcel(analysisSummary.AnalysisStatistics.SurplusEquitytoReinvestYr10),
  ]);
  data.push([
    "Buying power $",
    formatCurrencyForExcel(parseFloat(analysisSummary.AnalysisStatistics.BuyingPowerToday as any)),
    formatCurrencyForExcel(parseFloat(analysisSummary.AnalysisStatistics.BuyingPowerYr1 as any)),
    formatCurrencyForExcel(parseFloat(analysisSummary.AnalysisStatistics.BuyingPowerYr2 as any)),
    formatCurrencyForExcel(parseFloat(analysisSummary.AnalysisStatistics.BuyingPowerYr3 as any)),
    formatCurrencyForExcel(parseFloat(analysisSummary.AnalysisStatistics.BuyingPowerYr4 as any)),
    formatCurrencyForExcel(parseFloat(analysisSummary.AnalysisStatistics.BuyingPowerYr5 as any)),
    formatCurrencyForExcel(parseFloat(analysisSummary.AnalysisStatistics.BuyingPowerYr6 as any)),
    formatCurrencyForExcel(parseFloat(analysisSummary.AnalysisStatistics.BuyingPowerYr7 as any)),
    formatCurrencyForExcel(parseFloat(analysisSummary.AnalysisStatistics.BuyingPowerYr8 as any)),
    formatCurrencyForExcel(parseFloat(analysisSummary.AnalysisStatistics.BuyingPowerYr9 as any)),
    formatCurrencyForExcel(parseFloat(analysisSummary.AnalysisStatistics.BuyingPowerYr10 as any)),
  ]);
  data.push([
    "Rental income $",
    `$${analysisSummary.AnalysisStatistics.AverageWeeklyRentToday} pw`,
    formatCurrencyForExcel(analysisSummary.AnalysisStatistics.AverageWeeklyRentYr1),
    formatCurrencyForExcel(analysisSummary.AnalysisStatistics.AverageWeeklyRentYr2),
    formatCurrencyForExcel(analysisSummary.AnalysisStatistics.AverageWeeklyRentYr3),
    formatCurrencyForExcel(analysisSummary.AnalysisStatistics.AverageWeeklyRentYr4),
    formatCurrencyForExcel(analysisSummary.AnalysisStatistics.AverageWeeklyRentYr5),
    formatCurrencyForExcel(analysisSummary.AnalysisStatistics.AverageWeeklyRentYr6),
    formatCurrencyForExcel(analysisSummary.AnalysisStatistics.AverageWeeklyRentYr7),
    formatCurrencyForExcel(analysisSummary.AnalysisStatistics.AverageWeeklyRentYr8),
    formatCurrencyForExcel(analysisSummary.AnalysisStatistics.AverageWeeklyRentYr9),
    formatCurrencyForExcel(analysisSummary.AnalysisStatistics.AverageWeeklyRentYr10),
  ]);
  data.push([
    "Gross yield %",
    " ",
    formatPercentageForExcel(analysisSummary.AnalysisStatistics.GrossYieldYr1),
    formatPercentageForExcel(analysisSummary.AnalysisStatistics.GrossYieldYr2),
    formatPercentageForExcel(analysisSummary.AnalysisStatistics.GrossYieldYr3),
    formatPercentageForExcel(analysisSummary.AnalysisStatistics.GrossYieldYr4),
    formatPercentageForExcel(analysisSummary.AnalysisStatistics.GrossYieldYr5),
    formatPercentageForExcel(analysisSummary.AnalysisStatistics.GrossYieldYr6),
    formatPercentageForExcel(analysisSummary.AnalysisStatistics.GrossYieldYr7),
    formatPercentageForExcel(analysisSummary.AnalysisStatistics.GrossYieldYr8),
    formatPercentageForExcel(analysisSummary.AnalysisStatistics.GrossYieldYr9),
    formatPercentageForExcel(analysisSummary.AnalysisStatistics.GrossYieldYr10),
  ]);
  data.push([
    "Net yield %",
    " ",
    formatPercentageForExcel(analysisSummary.AnalysisStatistics.NetYieldYr1),
    formatPercentageForExcel(analysisSummary.AnalysisStatistics.NetYieldYr2),
    formatPercentageForExcel(analysisSummary.AnalysisStatistics.NetYieldYr3),
    formatPercentageForExcel(analysisSummary.AnalysisStatistics.NetYieldYr4),
    formatPercentageForExcel(analysisSummary.AnalysisStatistics.NetYieldYr5),
    formatPercentageForExcel(analysisSummary.AnalysisStatistics.NetYieldYr6),
    formatPercentageForExcel(analysisSummary.AnalysisStatistics.NetYieldYr7),
    formatPercentageForExcel(analysisSummary.AnalysisStatistics.NetYieldYr8),
    formatPercentageForExcel(analysisSummary.AnalysisStatistics.NetYieldYr9),
    formatPercentageForExcel(analysisSummary.AnalysisStatistics.NetYieldYr10),
  ]);
  data.push([]); // Empty row

  // Cash Deductions section
  data.push(["Cash deductions"]);
  data.push([
    "Property expenses $",
    formatCurrencyForExcel(analysisSummary.AnalysisCashDeduction.PropertyExpenseToday),
    formatCurrencyForExcel(analysisSummary.AnalysisCashDeduction.PropertyExpenseYr1),
    formatCurrencyForExcel(analysisSummary.AnalysisCashDeduction.PropertyExpenseYr2),
    formatCurrencyForExcel(analysisSummary.AnalysisCashDeduction.PropertyExpenseYr3),
    formatCurrencyForExcel(analysisSummary.AnalysisCashDeduction.PropertyExpenseYr4),
    formatCurrencyForExcel(analysisSummary.AnalysisCashDeduction.PropertyExpenseYr5),
    formatCurrencyForExcel(analysisSummary.AnalysisCashDeduction.PropertyExpenseYr6),
    formatCurrencyForExcel(analysisSummary.AnalysisCashDeduction.PropertyExpenseYr7),
    formatCurrencyForExcel(analysisSummary.AnalysisCashDeduction.PropertyExpenseYr8),
    formatCurrencyForExcel(analysisSummary.AnalysisCashDeduction.PropertyExpenseYr9),
    formatCurrencyForExcel(analysisSummary.AnalysisCashDeduction.PropertyExpenseYr10),
  ]);

  // Add loan details if available
  if (analysisSummary.AnalysisCashDeduction.CashDeductions) {
    analysisSummary.AnalysisCashDeduction.CashDeductions.forEach((loan) => {
      data.push([]); // Empty row before each loan
      data.push([`Loan ${loan.LoanNo}: ${loan.LoanName}`]);
      data.push([
        "Interest rate %",
        formatPercentageForExcel(loan.InterestRateToday),
        formatPercentageForExcel(loan.InterestRateYr1),
        formatPercentageForExcel(loan.InterestRateYr2),
        formatPercentageForExcel(loan.InterestRateYr3),
        formatPercentageForExcel(loan.InterestRateYr4),
        formatPercentageForExcel(loan.InterestRateYr5),
        formatPercentageForExcel(loan.InterestRateYr6),
        formatPercentageForExcel(loan.InterestRateYr7),
        formatPercentageForExcel(loan.InterestRateYr8),
        formatPercentageForExcel(loan.InterestRateYr9),
        formatPercentageForExcel(loan.InterestRateYr10),
      ]);
      data.push([
        "Interest payments $",
        formatCurrencyForExcel(loan.MortgageInterestPaymentsToday),
        formatCurrencyForExcel(loan.MortgageInterestPaymentsYr1),
        formatCurrencyForExcel(loan.MortgageInterestPaymentsYr2),
        formatCurrencyForExcel(loan.MortgageInterestPaymentsYr3),
        formatCurrencyForExcel(loan.MortgageInterestPaymentsYr4),
        formatCurrencyForExcel(loan.MortgageInterestPaymentsYr5),
        formatCurrencyForExcel(loan.MortgageInterestPaymentsYr6),
        formatCurrencyForExcel(loan.MortgageInterestPaymentsYr7),
        formatCurrencyForExcel(loan.MortgageInterestPaymentsYr8),
        formatCurrencyForExcel(loan.MortgageInterestPaymentsYr9),
        formatCurrencyForExcel(loan.MortgageInterestPaymentsYr10),
      ]);
      data.push([
        "Principal payments $",
        formatCurrencyForExcel(loan.PrincipalPaymentsToday),
        formatCurrencyForExcel(loan.PrincipalPaymentsYr1),
        formatCurrencyForExcel(loan.PrincipalPaymentsYr2),
        formatCurrencyForExcel(loan.PrincipalPaymentsYr3),
        formatCurrencyForExcel(loan.PrincipalPaymentsYr4),
        formatCurrencyForExcel(loan.PrincipalPaymentsYr5),
        formatCurrencyForExcel(loan.PrincipalPaymentsYr6),
        formatCurrencyForExcel(loan.PrincipalPaymentsYr7),
        formatCurrencyForExcel(loan.PrincipalPaymentsYr8),
        formatCurrencyForExcel(loan.PrincipalPaymentsYr9),
        formatCurrencyForExcel(loan.PrincipalPaymentsYr10),
      ]);
    });
  }

  data.push([
    "Pre-tax cash flow p/a $",
    " ",
    formatCurrencyForExcel(analysisSummary.AnalysisCashDeduction.PreTaxCashflowpaYr1),
    formatCurrencyForExcel(analysisSummary.AnalysisCashDeduction.PreTaxCashflowpaYr2),
    formatCurrencyForExcel(analysisSummary.AnalysisCashDeduction.PreTaxCashflowpaYr3),
    formatCurrencyForExcel(analysisSummary.AnalysisCashDeduction.PreTaxCashflowpaYr4),
    formatCurrencyForExcel(analysisSummary.AnalysisCashDeduction.PreTaxCashflowpaYr5),
    formatCurrencyForExcel(analysisSummary.AnalysisCashDeduction.PreTaxCashflowpaYr6),
    formatCurrencyForExcel(analysisSummary.AnalysisCashDeduction.PreTaxCashflowpaYr7),
    formatCurrencyForExcel(analysisSummary.AnalysisCashDeduction.PreTaxCashflowpaYr8),
    formatCurrencyForExcel(analysisSummary.AnalysisCashDeduction.PreTaxCashflowpaYr9),
    formatCurrencyForExcel(analysisSummary.AnalysisCashDeduction.PreTaxCashflowpaYr10),
  ]);
  data.push([
    "Pre-tax cash flow p/w $",
    " ",
    formatCurrencyForExcel(analysisSummary.AnalysisCashDeduction.PreTaxCashflowpwYr1),
    formatCurrencyForExcel(analysisSummary.AnalysisCashDeduction.PreTaxCashflowpwYr2),
    formatCurrencyForExcel(analysisSummary.AnalysisCashDeduction.PreTaxCashflowpwYr3),
    formatCurrencyForExcel(analysisSummary.AnalysisCashDeduction.PreTaxCashflowpwYr4),
    formatCurrencyForExcel(analysisSummary.AnalysisCashDeduction.PreTaxCashflowpwYr5),
    formatCurrencyForExcel(analysisSummary.AnalysisCashDeduction.PreTaxCashflowpwYr6),
    formatCurrencyForExcel(analysisSummary.AnalysisCashDeduction.PreTaxCashflowpwYr7),
    formatCurrencyForExcel(analysisSummary.AnalysisCashDeduction.PreTaxCashflowpwYr8),
    formatCurrencyForExcel(analysisSummary.AnalysisCashDeduction.PreTaxCashflowpwYr9),
    formatCurrencyForExcel(analysisSummary.AnalysisCashDeduction.PreTaxCashflowpwYr10),
  ]);
  data.push([]); // Empty row

  // Non-cash deductions section
  data.push(["Non-cash deductions"]);
  data.push([
    "Depreciation $",
    " ",
    formatCurrencyForExcel(analysisSummary.AnalysisNonCashDeduction.DepreciationYr1),
    formatCurrencyForExcel(analysisSummary.AnalysisNonCashDeduction.DepreciationYr2),
    formatCurrencyForExcel(analysisSummary.AnalysisNonCashDeduction.DepreciationYr3),
    formatCurrencyForExcel(analysisSummary.AnalysisNonCashDeduction.DepreciationYr4),
    formatCurrencyForExcel(analysisSummary.AnalysisNonCashDeduction.DepreciationYr5),
    formatCurrencyForExcel(analysisSummary.AnalysisNonCashDeduction.DepreciationYr6),
    formatCurrencyForExcel(analysisSummary.AnalysisNonCashDeduction.DepreciationYr7),
    formatCurrencyForExcel(analysisSummary.AnalysisNonCashDeduction.DepreciationYr8),
    formatCurrencyForExcel(analysisSummary.AnalysisNonCashDeduction.DepreciationYr9),
    formatCurrencyForExcel(analysisSummary.AnalysisNonCashDeduction.DepreciationYr10),
  ]);
  data.push([]); // Empty row

  // Summary section
  data.push(["Summary"]);
  data.push([
    "Total deductions $",
    " ",
    formatCurrencyForExcel(analysisSummary.AnalysisSummary.TotalDeductionYr1),
    formatCurrencyForExcel(analysisSummary.AnalysisSummary.TotalDeductionYr2),
    formatCurrencyForExcel(analysisSummary.AnalysisSummary.TotalDeductionYr3),
    formatCurrencyForExcel(analysisSummary.AnalysisSummary.TotalDeductionYr4),
    formatCurrencyForExcel(analysisSummary.AnalysisSummary.TotalDeductionYr5),
    formatCurrencyForExcel(analysisSummary.AnalysisSummary.TotalDeductionYr6),
    formatCurrencyForExcel(analysisSummary.AnalysisSummary.TotalDeductionYr7),
    formatCurrencyForExcel(analysisSummary.AnalysisSummary.TotalDeductionYr8),
    formatCurrencyForExcel(analysisSummary.AnalysisSummary.TotalDeductionYr9),
    formatCurrencyForExcel(analysisSummary.AnalysisSummary.TotalDeductionYr10),
  ]);
  data.push([
    "Net profit/loss $",
    " ",
    formatCurrencyForExcel(analysisSummary.AnalysisSummary.NetProfitLossYr1),
    formatCurrencyForExcel(analysisSummary.AnalysisSummary.NetProfitLossYr2),
    formatCurrencyForExcel(analysisSummary.AnalysisSummary.NetProfitLossYr3),
    formatCurrencyForExcel(analysisSummary.AnalysisSummary.NetProfitLossYr4),
    formatCurrencyForExcel(analysisSummary.AnalysisSummary.NetProfitLossYr5),
    formatCurrencyForExcel(analysisSummary.AnalysisSummary.NetProfitLossYr6),
    formatCurrencyForExcel(analysisSummary.AnalysisSummary.NetProfitLossYr7),
    formatCurrencyForExcel(analysisSummary.AnalysisSummary.NetProfitLossYr8),
    formatCurrencyForExcel(analysisSummary.AnalysisSummary.NetProfitLossYr9),
    formatCurrencyForExcel(analysisSummary.AnalysisSummary.NetProfitLossYr10),
  ]);
  data.push([
    "Tax refund $",
    " ",
    formatCurrencyForExcel(analysisSummary.AnalysisSummary.TaxRefundYr1),
    formatCurrencyForExcel(analysisSummary.AnalysisSummary.TaxRefundYr2),
    formatCurrencyForExcel(analysisSummary.AnalysisSummary.TaxRefundYr3),
    formatCurrencyForExcel(analysisSummary.AnalysisSummary.TaxRefundYr4),
    formatCurrencyForExcel(analysisSummary.AnalysisSummary.TaxRefundYr5),
    formatCurrencyForExcel(analysisSummary.AnalysisSummary.TaxRefundYr6),
    formatCurrencyForExcel(analysisSummary.AnalysisSummary.TaxRefundYr7),
    formatCurrencyForExcel(analysisSummary.AnalysisSummary.TaxRefundYr8),
    formatCurrencyForExcel(analysisSummary.AnalysisSummary.TaxRefundYr9),
    formatCurrencyForExcel(analysisSummary.AnalysisSummary.TaxRefundYr10),
  ]);
  data.push([
    "Land Tax $",
    " ",
    formatCurrencyForExcel(analysisSummary.LandTax.LandTaxYr1),
    formatCurrencyForExcel(analysisSummary.LandTax.LandTaxYr2),
    formatCurrencyForExcel(analysisSummary.LandTax.LandTaxYr3),
    formatCurrencyForExcel(analysisSummary.LandTax.LandTaxYr4),
    formatCurrencyForExcel(analysisSummary.LandTax.LandTaxYr5),
    formatCurrencyForExcel(analysisSummary.LandTax.LandTaxYr6),
    formatCurrencyForExcel(analysisSummary.LandTax.LandTaxYr7),
    formatCurrencyForExcel(analysisSummary.LandTax.LandTaxYr8),
    formatCurrencyForExcel(analysisSummary.LandTax.LandTaxYr9),
    formatCurrencyForExcel(analysisSummary.LandTax.LandTaxYr10),
  ]);
  data.push([
    "After tax cash flow p/a $",
    " ",
    formatCurrencyForExcel(analysisSummary.AnalysisSummary.PostTaxCashflowPAYr1),
    formatCurrencyForExcel(analysisSummary.AnalysisSummary.PostTaxCashflowPAYr2),
    formatCurrencyForExcel(analysisSummary.AnalysisSummary.PostTaxCashflowPAYr3),
    formatCurrencyForExcel(analysisSummary.AnalysisSummary.PostTaxCashflowPAYr4),
    formatCurrencyForExcel(analysisSummary.AnalysisSummary.PostTaxCashflowPAYr5),
    formatCurrencyForExcel(analysisSummary.AnalysisSummary.PostTaxCashflowPAYr6),
    formatCurrencyForExcel(analysisSummary.AnalysisSummary.PostTaxCashflowPAYr7),
    formatCurrencyForExcel(analysisSummary.AnalysisSummary.PostTaxCashflowPAYr8),
    formatCurrencyForExcel(analysisSummary.AnalysisSummary.PostTaxCashflowPAYr9),
    formatCurrencyForExcel(analysisSummary.AnalysisSummary.PostTaxCashflowPAYr10),
  ]);
  data.push([
    "After tax cash flow p/w $",
    " ",
    formatCurrencyForExcel(analysisSummary.AnalysisSummary.PostTaxCashflowPWYr1),
    formatCurrencyForExcel(analysisSummary.AnalysisSummary.PostTaxCashflowPWYr2),
    formatCurrencyForExcel(analysisSummary.AnalysisSummary.PostTaxCashflowPWYr3),
    formatCurrencyForExcel(analysisSummary.AnalysisSummary.PostTaxCashflowPWYr4),
    formatCurrencyForExcel(analysisSummary.AnalysisSummary.PostTaxCashflowPWYr5),
    formatCurrencyForExcel(analysisSummary.AnalysisSummary.PostTaxCashflowPWYr6),
    formatCurrencyForExcel(analysisSummary.AnalysisSummary.PostTaxCashflowPWYr7),
    formatCurrencyForExcel(analysisSummary.AnalysisSummary.PostTaxCashflowPWYr8),
    formatCurrencyForExcel(analysisSummary.AnalysisSummary.PostTaxCashflowPWYr9),
    formatCurrencyForExcel(analysisSummary.AnalysisSummary.PostTaxCashflowPWYr10),
  ]);

  // Create worksheet
  const worksheet = XLSX.utils.aoa_to_sheet(data);

  // Set column widths for the layout
  const wscols = [
    { wch: 30 }, // Column A - Labels
    { wch: 15 }, // Column B - Values
    { wch: 12 }, // Column C - Year 1
    { wch: 12 }, // Column D - Year 2
    { wch: 12 }, // Column E - Year 3
    { wch: 12 }, // Column F - Year 4
    { wch: 12 }, // Column G - Year 5
    { wch: 12 }, // Column H - Year 6
    { wch: 12 }, // Column I - Year 7
    { wch: 12 }, // Column J - Year 8
    { wch: 12 }, // Column K - Year 9
    { wch: 12 }, // Column L - Year 10
  ];
  worksheet["!cols"] = wscols;

  // Apply cell styles
  const range = XLSX.utils.decode_range(worksheet["!ref"] || "A1");

  for (let R = range.s.r; R <= range.e.r; ++R) {
    for (let C = range.s.c; C <= range.e.c; ++C) {
      const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
      if (!worksheet[cellAddress]) continue;

      const cell = worksheet[cellAddress];

      // Initialize cell style
      if (!cell.s) cell.s = {};

      // Special styling for the Variables/Summary Input rows (A3:A9 and B3:B9)
      // Row 0 = header, Row 1 = empty, Row 2-8 = data rows (A3:A9 in Excel)
      // THIS MUST BE CHECKED FIRST to avoid being overwritten
      if (R >= 2 && R <= 8 && C <= 1) {
        if (C === 0) {
          // Blue background for labels (column A, rows 3-9)
          cell.s = {
            font: { bold: true, sz: 11, color: { rgb: "FFFFFF" } },
            fill: { fgColor: { rgb: "4472C4" } },
            alignment: { vertical: "center", horizontal: "left" },
            border: {
              top: { style: "thin", color: { rgb: "000000" } },
              bottom: { style: "thin", color: { rgb: "000000" } },
              left: { style: "thin", color: { rgb: "000000" } },
              right: { style: "thin", color: { rgb: "000000" } },
            },
          };
        } else if (C === 1) {
          // White background for values (column B, rows 3-9)
          cell.s = {
            font: { sz: 11 },
            fill: { fgColor: { rgb: "FFFFFF" } },
            alignment: { vertical: "center", horizontal: "right" },
            border: {
              top: { style: "thin", color: { rgb: "000000" } },
              bottom: { style: "thin", color: { rgb: "000000" } },
              left: { style: "thin", color: { rgb: "000000" } },
              right: { style: "thin", color: { rgb: "000000" } },
            },
          };
        }
        continue; // Skip other styling for these cells
      }

      // Style for header row (Results for...)
      if (R === 0) {
        cell.s = {
          font: { bold: true, sz: 14, color: { rgb: "2E5090" } },
          alignment: { vertical: "center", horizontal: "left" },
        };
        continue;
      }

      // Style for section headers (Overview, Statistics, etc.)
      if (
        data[R] &&
        data[R][C] &&
        (data[R][C] === "Overview" ||
          data[R][C] === "Statistics" ||
          data[R][C] === "Cash deductions" ||
          data[R][C] === "Non-cash deductions" ||
          data[R][C] === "Summary" ||
          data[R][C] === "Years")
      ) {
        cell.s = {
          font: { bold: true, sz: 11, color: { rgb: "FFFFFF" } },
          fill: { fgColor: { rgb: "4472C4" } },
          alignment: { vertical: "center", horizontal: "left" },
          border: {
            top: { style: "thin", color: { rgb: "000000" } },
            bottom: { style: "thin", color: { rgb: "000000" } },
            left: { style: "thin", color: { rgb: "000000" } },
            right: { style: "thin", color: { rgb: "000000" } },
          },
        };
        continue;
      }

      // Style for year headers row (Today, 1, 2, 3...)
      if (data[R] && data[R][0] === "Years") {
        cell.s = {
          font: { bold: true, sz: 10, color: { rgb: "FFFFFF" } },
          fill: { fgColor: { rgb: "4472C4" } },
          alignment: { vertical: "center", horizontal: "center" },
          border: {
            top: { style: "thin", color: { rgb: "000000" } },
            bottom: { style: "thin", color: { rgb: "000000" } },
            left: { style: "thin", color: { rgb: "000000" } },
            right: { style: "thin", color: { rgb: "000000" } },
          },
        };
        continue;
      }
      // Style for data rows with alternating background (after Years header)
      else if (R > 8 && data[R] && data[R][C] && data[R][0] !== "Years") {
        const isEvenRow = R % 2 === 0;
        const bgColor = isEvenRow ? "F2F2F2" : "FFFFFF";

        cell.s = {
          fill: { fgColor: { rgb: bgColor } },
          alignment: {
            vertical: "center",
            horizontal: C === 0 ? "left" : "right",
          },
          border: {
            top: { style: "thin", color: { rgb: "D3D3D3" } },
            bottom: { style: "thin", color: { rgb: "D3D3D3" } },
            left: { style: "thin", color: { rgb: "D3D3D3" } },
            right: { style: "thin", color: { rgb: "D3D3D3" } },
          },
        };

        // Bold and blue color for row labels (first column)
        if (C === 0 && data[R][C]) {
          cell.s.font = { bold: true, color: { rgb: "2E5090" } };
        }

        // Red color for negative values (data columns)
        if (C > 0 && isNegativeValue(data[R][C])) {
          cell.s.font = { color: { rgb: "FF0000" } };
        }
      }

      // Style for loan name rows
      if (data[R] && data[R][C] && typeof data[R][C] === "string" && data[R][C].startsWith("Loan")) {
        cell.s = {
          font: { bold: true, sz: 11, color: { rgb: "2E5090" } },
          fill: { fgColor: { rgb: "E7E6E6" } },
          alignment: { vertical: "center", horizontal: "left" },
        };
      }
    }
  }

  // Add worksheet to workbook
  XLSX.utils.book_append_sheet(workbook, worksheet, "Analysis Report");

  // Generate filename with timestamp
  const timestamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, -5);
  const fileName = `Property_Analysis_${timestamp}.xlsx`;

  // Write and download file
  XLSX.writeFile(workbook, fileName);
};
